import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Header from "../../Components/UI/Header";
import { getUserRoutes, routesConfig } from "../../routes";

import { makeStyles } from "@mui/styles";
import "../../App.css";
import ModuleSelectionPopup from "../../Components/Modal/ModuleSelectionPopup";
import { loggedInUserInfoSliceActions } from "../../Store/Slices/LoggedInUserInfo";
import { tilePermissions } from "../../Js/ConstantsOptions";

/* ------------------------------------------------------------
 * STYLES
 * -----------------------------------------------------------*/

const DRAWER_EXPANDED = 220;
const DRAWER_COLLAPSED = 70;

const DOCUMENTS_MENU_ITEM = {
  label: "Documents",
  path: "/Documents",
  icon: {
    active: "/menuicon/repository_active.png",
    inactive: "/menuicon/repository_inactive.png",
  },
};

/* ------------------------------------------------------------
 * HELPERS
 * -----------------------------------------------------------*/
const hasPermission = (permissionName, userPermission) =>
  !permissionName || userPermission?.includes(permissionName);

const shouldShowRoute = (route, role, userPermission) => {
  if (!route) return false;
  if (route.hidden) return false;
  if (route.path?.toLowerCase() === "profile") return false;

  if (route.roles?.length) {
    const allowed = route.roles.map((r) => r.toLowerCase());
    if (!allowed.includes(role)) return false;
  }

  if (!hasPermission(route.permission, userPermission)) return false;

  if (route.children?.length) {
    return route.children.some((child) =>
      shouldShowRoute(child, role, userPermission),
    );
  }

  return true;
};

const createMenuItem = (route) => {
  if (route.path?.toLowerCase() === "documents") {
    return { ...route, ...DOCUMENTS_MENU_ITEM };
  }

  return {
    ...route,
    label: route.label || route.path || "",
    path: route.path ? `/${route.path}` : "/",
    icon: route.icon || null,
  };
};

const renderIcon = (icon, isActive, classes, drawerOpen) => {
  if (!icon) return <MenuIcon />;

  if (icon.active && icon.inactive) {
    return (
      <img
        src={isActive ? icon.active : icon.inactive}
        className={drawerOpen ? classes.image : classes.iconImg}
        alt=""
      />
    );
  }

  if (typeof icon === "string") {
    return <img src={icon} className={classes.image} alt="" />;
  }

  if (typeof icon === "function") {
    return React.createElement(icon);
  }

  return <MenuIcon />;
};

/* ------------------------------------------------------------
 * MAIN COMPONENT
 * -----------------------------------------------------------*/
const MainStageLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(true);
  const [open, setOpen] = useState(false);
  const [tileAllPermissions, setTileAllPermissions] = useState(null);
  const isInstituteListPage =
    location.pathname === "/Institutes" ||
    location.pathname === "/Institutes/" ||
    location.pathname.startsWith("/Institutes/Filter");

  const {
    userInfo,
    isPayrollApprover,
    userPermission = [],
    currentSelectedModule,
  } = useSelector((s) => s.loggedInUserInfo);

  const role = userInfo?.rolesInfo?.title?.toLowerCase() || "";

  useEffect(() => {
    if (userInfo) {
      const userRole = userInfo.rolesInfo?.title?.toLowerCase();
      const foundRole = tilePermissions.find((option) => option.role === userRole);

      if (!foundRole) return;

      const permittedTiles = [...foundRole.tiles];      
      
      if (
        userRole === "trainer" &&
        userInfo.employeeProfile &&
        !permittedTiles.includes("employee")
      ) {
        permittedTiles.push("employee");
      }

      setTileAllPermissions(permittedTiles);

      if (permittedTiles.length > 1) {
        setOpen(true);
      } else if (permittedTiles.length === 1) {
        dispatch(
          loggedInUserInfoSliceActions.setCurrentSelectedModule({
            id: permittedTiles[0],
          }),
        );
      }
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    if (
      !currentSelectedModule &&
      tileAllPermissions &&
      tileAllPermissions.length > 1
    ) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [currentSelectedModule, tileAllPermissions]);


  const menuItems = useMemo(() => {
    if (currentSelectedModule) {
      const roleRoutes = getUserRoutes(routesConfig, role, isPayrollApprover);
      return roleRoutes
        .filter((route) => shouldShowRoute(route, role, userPermission))
        .filter(
          (route) =>
            route?.module?.indexOf(currentSelectedModule.id) > -1,
        )
        .map(createMenuItem);
    }
  }, [role, userPermission, currentSelectedModule, isPayrollApprover]);

  const handleModuleSelect = (module) => {
    dispatch(loggedInUserInfoSliceActions.setCurrentSelectedModule(module));
    setOpen(false);

    const roleRoutes = getUserRoutes(routesConfig, role, isPayrollApprover);

    const firstRoute = roleRoutes.find(
      (route) => route.module?.includes(module.id)
    );

    if (firstRoute) {
      navigate(`/${firstRoute.path}`);
    }
  };

  /* ------------------------------------------------------------
   * SIDEBAR MENU
   * -----------------------------------------------------------*/
  const alignMenu = (
    <List>
      {/* Logo */}
      <ListItem disablePadding sx={{ mb: 0.2 }}>
        <ListItemButton
          sx={{
            justifyContent: drawerOpen ? "space-between" : "center",
            px: 2,
          }}
        >
          {drawerOpen && (
            <ListItemIcon
              onClick={() => navigate(menuItems[0]?.path || "/Dashboard")}
            >
              <img
                src="/WW_Full.png"
                className={drawerOpen ? classes.logoImg : classes.logoImgSmall}
                alt="Words Worth"
              />
            </ListItemIcon>
          )}
          {/* Toggle Button */}
          <ListItem disablePadding sx={{ justifyContent: "center", py: 1 }}>
            <IconButton
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{
                color: "#FFFFFF",
                paddingLeft: 1,
                "&:hover": {
                  color: "#90CAF9", // icon color on hover
                  backgroundColor: "rgba(255, 255, 255, 0.15)", // hover bg
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </ListItem>
        </ListItemButton>
      </ListItem>

      {/* Menu Items */}
      {currentSelectedModule &&
        menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);

          return (
            <Tooltip
              key={item.path}
              title={!drawerOpen ? item.label : ""}
              placement="right"
            >
              <ListItem disablePadding className={isActive ? "Menustyle" : ""}>
                <ListItemButton
                  selected={isActive}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: drawerOpen ? "initial" : "center",
                    px: drawerOpen ? 2 : 1,
                  }}
                >
                  <ListItemIcon>
                    {renderIcon(item.icon, isActive, classes, drawerOpen)}
                  </ListItemIcon>

                  {drawerOpen && (
                    <ListItemText
                      className={isActive ? "text" : "textunselect"}
                    >
                      <Typography sx={{ fontSize: "0.9vw", fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </ListItemText>
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
    </List>
  );

  /* ------------------------------------------------------------
   * RENDER
   * -----------------------------------------------------------*/
  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* MINI DRAWER */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerOpen ? DRAWER_EXPANDED : DRAWER_COLLAPSED,
          flexShrink: 0,
          whiteSpace: "nowrap",
          transition: "width 0.3s",
          "& .MuiDrawer-paper": {
            width: drawerOpen ? DRAWER_EXPANDED : DRAWER_COLLAPSED,
            transition: "width 0.3s",
            overflowX: "hidden",
            // backgroundColor: "#F5F6FA"
            backgroundColor: "#3851A8",
          },
        }}
      >
        {alignMenu}
      </Drawer>

      {/* MAIN CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          px: 2,
          pb: 1,
        }}
        bgcolor="#f5f7fb"
      >
        <Header
          tileAllPermissions={tileAllPermissions}
          tileOpen={() => setOpen(true)}
        />
        <Box
          sx={{
            mt: 2,
            flex: 1,
            minHeight: 0,
            height: "auto",
            overflow: isInstituteListPage ? "hidden" : "auto",
          }}
          className={classes.scrollBar}
        >
          <Outlet context={{ drawerOpen }} />
          {/* context={{ drawerOpen }} */}
        </Box>
      </Box>
      {tileAllPermissions && (
        <ModuleSelectionPopup
          open={open}
          onClose={() => setOpen(false)}
          onSelect={handleModuleSelect}
          roleTiles={tileAllPermissions}
        ></ModuleSelectionPopup>
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  image: { width: "2.4vw" },
  logoImg: {
    width: "6rem",
    transition: "width 0.3s",
    "@media (min-width: 1636px)": { width: "10rem" },
  },
  logoImgSmall: {
    width: "3rem", // smaller logo when drawer collapsed
    transition: "width 0.3s",
    alignItems: "center",
    "@media (min-width: 1636px)": { width: "4rem" },
  },
  iconImg: {
    width: "2.6vw", // smaller logo when drawer collapsed
    transition: "width 0.3s",
    paddingLeft: 10,
    "@media (min-width: 1636px)": { width: "4rem" },
  },
  scrollBar: {
    height: "90vh",
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "0.4em",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#adbdff",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
      borderRadius: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#3751A8",
      borderRadius: 5,
    },
  },
  scrollBarHeight: {
    "&::-webkit-scrollbar": {
      height: "0.6em",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#adbdff",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
      borderRadius: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#3751A8",
      borderRadius: 5,
    },
  },
});

export default MainStageLayout;
